import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./MastersPage.css";

type SupplierRow = {
  id: string;
  supplier_type: "person" | "company";
  document_type: string | null;
  document_number: string | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  city: string | null;
  country_code: string;
  status: string;
  updated_at: string;
};

type SupplierDetail = SupplierRow & {
  organization_id: string;
  address: string | null;
  notes: string | null;
  created_at: string;
};

type WarehouseRow = {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  stock_items: number | string;
  total_quantity: number | string;
  updated_at: string;
};

export function MastersPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRow[]>([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showWarehouseForm, setShowWarehouseForm] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);
  const [editingWarehouseId, setEditingWarehouseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<"supplier" | "warehouse" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [supplierType, setSupplierType] = useState<"person" | "company">(
    "company",
  );
  const [supplierName, setSupplierName] = useState("");
  const [supplierDocumentType, setSupplierDocumentType] = useState("RUC");
  const [supplierDocumentNumber, setSupplierDocumentNumber] = useState("");
  const [supplierContact, setSupplierContact] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierPhone, setSupplierPhone] = useState("");
  const [supplierWhatsapp, setSupplierWhatsapp] = useState("");
  const [supplierAddress, setSupplierAddress] = useState("");
  const [supplierCity, setSupplierCity] = useState("");
  const [supplierCountryCode, setSupplierCountryCode] = useState("PE");
  const [supplierNotes, setSupplierNotes] = useState("");

  const [warehouseCode, setWarehouseCode] = useState("");
  const [warehouseName, setWarehouseName] = useState("");
  const [warehouseAddress, setWarehouseAddress] = useState("");

  const load = useCallback(async () => {
    if (!organizationId) {
      setSuppliers([]);
      setWarehouses([]);
      return;
    }

    setLoading(true);
    setError(null);

    const [supplierResult, warehouseResult] = await Promise.all([
      supabase.rpc("list_organization_suppliers", {
        p_organization_id: organizationId,
        p_search: supplierSearch.trim() || null,
      }),
      supabase.rpc("list_organization_warehouses", {
        p_organization_id: organizationId,
        p_search: warehouseSearch.trim() || null,
      }),
    ]);

    const firstError = supplierResult.error ?? warehouseResult.error;

    if (firstError) setError(firstError.message);

    setSuppliers(
      supplierResult.error
        ? []
        : ((supplierResult.data ?? []) as SupplierRow[]),
    );
    setWarehouses(
      warehouseResult.error
        ? []
        : ((warehouseResult.data ?? []) as WarehouseRow[]),
    );
    setLoading(false);
  }, [organizationId, supplierSearch, warehouseSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 220);
    return () => window.clearTimeout(timer);
  }, [load]);

  function resetSupplierForm() {
    setSupplierType("company");
    setSupplierName("");
    setSupplierDocumentType("RUC");
    setSupplierDocumentNumber("");
    setSupplierContact("");
    setSupplierEmail("");
    setSupplierPhone("");
    setSupplierWhatsapp("");
    setSupplierAddress("");
    setSupplierCity("");
    setSupplierCountryCode("PE");
    setSupplierNotes("");
    setEditingSupplierId(null);
    setShowSupplierForm(false);
  }

  function resetWarehouseForm() {
    setWarehouseCode("");
    setWarehouseName("");
    setWarehouseAddress("");
    setEditingWarehouseId(null);
    setShowWarehouseForm(false);
  }

  async function startEditSupplier(supplierId: string) {
    setBusy("supplier");
    setError(null);
    setSuccess(null);

    const { data, error: rpcError } = await supabase.rpc(
      "get_supplier_detail",
      { p_supplier_id: supplierId },
    );

    setBusy(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const supplier = data as SupplierDetail;
    setEditingSupplierId(supplier.id);
    setSupplierType(supplier.supplier_type);
    setSupplierName(supplier.name);
    setSupplierDocumentType(supplier.document_type ?? "RUC");
    setSupplierDocumentNumber(supplier.document_number ?? "");
    setSupplierContact(supplier.contact_name ?? "");
    setSupplierEmail(supplier.email ?? "");
    setSupplierPhone(supplier.phone ?? "");
    setSupplierWhatsapp(supplier.whatsapp ?? "");
    setSupplierAddress(supplier.address ?? "");
    setSupplierCity(supplier.city ?? "");
    setSupplierCountryCode(supplier.country_code ?? "PE");
    setSupplierNotes(supplier.notes ?? "");
    setShowSupplierForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEditWarehouse(warehouse: WarehouseRow) {
    setEditingWarehouseId(warehouse.id);
    setWarehouseCode(warehouse.code);
    setWarehouseName(warehouse.name);
    setWarehouseAddress(warehouse.address ?? "");
    setShowWarehouseForm(true);
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function createSupplier(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    setBusy("supplier");
    setError(null);
    setSuccess(null);

    const payload = {
      p_supplier_type: supplierType,
      p_name: supplierName,
      p_document_type: supplierDocumentNumber.trim()
        ? supplierDocumentType
        : null,
      p_document_number: supplierDocumentNumber.trim() || null,
      p_contact_name: supplierContact.trim() || null,
      p_email: supplierEmail.trim() || null,
      p_phone: supplierPhone.trim() || null,
      p_whatsapp: supplierWhatsapp.trim() || null,
      p_address: supplierAddress.trim() || null,
      p_city: supplierCity.trim() || null,
      p_country_code: supplierCountryCode.trim().toUpperCase() || "PE",
      p_notes: supplierNotes.trim() || null,
    };

    const { error: rpcError } = editingSupplierId
      ? await supabase.rpc("update_organization_supplier", {
          p_supplier_id: editingSupplierId,
          ...payload,
        })
      : await supabase.rpc("create_organization_supplier", {
          p_organization_id: organizationId,
          ...payload,
        });

    setBusy(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const wasEditing = Boolean(editingSupplierId);
    resetSupplierForm();
    setSuccess(wasEditing ? "Proveedor actualizado." : "Proveedor creado.");
    await load();
  }

  async function createWarehouse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    setBusy("warehouse");
    setError(null);
    setSuccess(null);

    const { error: rpcError } = editingWarehouseId
      ? await supabase.rpc("update_inventory_warehouse", {
          p_warehouse_id: editingWarehouseId,
          p_code: warehouseCode,
          p_name: warehouseName,
          p_address: warehouseAddress.trim() || null,
        })
      : await supabase.rpc("create_inventory_warehouse", {
          p_organization_id: organizationId,
          p_code: warehouseCode,
          p_name: warehouseName,
          p_address: warehouseAddress.trim() || null,
        });

    setBusy(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    const wasEditing = Boolean(editingWarehouseId);
    resetWarehouseForm();
    setSuccess(wasEditing ? "Almacén actualizado." : "Almacén creado.");
    await load();
  }

  async function changeSupplierStatus(supplier: SupplierRow) {
    setBusy("supplier");
    setError(null);
    setSuccess(null);

    const nextStatus = supplier.status === "active" ? "inactive" : "active";
    const { error: rpcError } = await supabase.rpc("set_supplier_status", {
      p_supplier_id: supplier.id,
      p_status: nextStatus,
    });

    setBusy(null);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess(
      nextStatus === "active" ? "Proveedor activado." : "Proveedor desactivado.",
    );
    await load();
  }

  async function changeWarehouseStatus(warehouse: WarehouseRow) {
    setBusy("warehouse");
    setError(null);
    setSuccess(null);

    const nextStatus = warehouse.status === "active" ? "inactive" : "active";
    const { error: rpcError } = await supabase.rpc("set_warehouse_status", {
      p_warehouse_id: warehouse.id,
      p_status: nextStatus,
    });

    setBusy(null);

    if (rpcError) {
      if (rpcError.message.includes("WAREHOUSE_HAS_STOCK")) {
        setError("No se puede desactivar el almacén porque todavía tiene stock o reservas.");
      } else if (rpcError.message.includes("WAREHOUSE_HAS_OPEN_PURCHASE_ORDERS")) {
        setError("No se puede desactivar el almacén porque tiene órdenes de compra abiertas.");
      } else {
        setError(rpcError.message);
      }
      return;
    }

    setSuccess(
      nextStatus === "active" ? "Almacén activado." : "Almacén desactivado.",
    );
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Maestros</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Proveedores y almacenes utilizados por Compras e Inventario.</p>
        </div>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? <div className="form-success masters-message">{success}</div> : null}

      <section className="masters-layout">
        <div className="panel-card">
          <div className="section-heading masters-heading">
            <div>
              <span className="eyebrow">Proveedores</span>
              <h2>{loading ? "…" : suppliers.length} registrados</h2>
            </div>
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                if (showSupplierForm) {
                  resetSupplierForm();
                } else {
                  resetSupplierForm();
                  setShowSupplierForm(true);
                }
              }}
            >
              {showSupplierForm ? "Cerrar" : "Nuevo proveedor"}
            </button>
          </div>

          {showSupplierForm ? (
            <>
              {editingSupplierId ? (
                <div className="form-success masters-message">
                  Editando proveedor existente.
                </div>
              ) : null}
              <form className="masters-form" onSubmit={createSupplier}>
              <label>
                Tipo
                <select
                  value={supplierType}
                  onChange={(event) =>
                    setSupplierType(
                      event.target.value as "person" | "company",
                    )
                  }
                >
                  <option value="company">Empresa</option>
                  <option value="person">Persona</option>
                </select>
              </label>

              <label className="masters-wide">
                Nombre / razón social
                <input
                  required
                  value={supplierName}
                  onChange={(event) => setSupplierName(event.target.value)}
                />
              </label>

              <label>
                Tipo documento
                <select
                  value={supplierDocumentType}
                  onChange={(event) =>
                    setSupplierDocumentType(event.target.value)
                  }
                >
                  <option value="RUC">RUC</option>
                  <option value="DNI">DNI</option>
                  <option value="FOREIGN_TAX_ID">ID fiscal extranjero</option>
                  <option value="OTHER">Otro</option>
                </select>
              </label>

              <label>
                Número documento
                <input
                  value={supplierDocumentNumber}
                  onChange={(event) =>
                    setSupplierDocumentNumber(event.target.value)
                  }
                />
              </label>

              <label>
                Contacto
                <input
                  value={supplierContact}
                  onChange={(event) => setSupplierContact(event.target.value)}
                />
              </label>

              <label>
                Correo
                <input
                  type="email"
                  value={supplierEmail}
                  onChange={(event) => setSupplierEmail(event.target.value)}
                />
              </label>

              <label>
                Teléfono
                <input
                  value={supplierPhone}
                  onChange={(event) => setSupplierPhone(event.target.value)}
                />
              </label>

              <label>
                Ciudad
                <input
                  value={supplierCity}
                  onChange={(event) => setSupplierCity(event.target.value)}
                />
              </label>

              <label>
                WhatsApp
                <input
                  value={supplierWhatsapp}
                  onChange={(event) => setSupplierWhatsapp(event.target.value)}
                />
              </label>

              <label>
                País
                <input
                  maxLength={2}
                  value={supplierCountryCode}
                  onChange={(event) =>
                    setSupplierCountryCode(event.target.value.toUpperCase())
                  }
                />
              </label>

              <label className="masters-wide">
                Dirección
                <input
                  value={supplierAddress}
                  onChange={(event) => setSupplierAddress(event.target.value)}
                />
              </label>

              <label className="masters-wide">
                Notas
                <input
                  value={supplierNotes}
                  onChange={(event) => setSupplierNotes(event.target.value)}
                />
              </label>

              <div className="masters-actions">
                <button
                  className="primary-button"
                  type="submit"
                  disabled={busy === "supplier"}
                >
                  {busy === "supplier"
                    ? "Guardando…"
                    : editingSupplierId
                      ? "Guardar cambios"
                      : "Guardar proveedor"}
                </button>
                {editingSupplierId ? (
                  <button
                    className="ghost-button"
                    type="button"
                    onClick={resetSupplierForm}
                  >
                    Cancelar edición
                  </button>
                ) : null}
              </div>
            </form>
            </>
          ) : null}

          <input
            className="masters-search"
            type="search"
            placeholder="Buscar proveedor"
            value={supplierSearch}
            onChange={(event) => setSupplierSearch(event.target.value)}
          />

          <div className="masters-list">
            {suppliers.map((supplier) => (
              <article key={supplier.id}>
                <div>
                  <span className="status-badge">{supplier.status}</span>
                  <strong>{supplier.name}</strong>
                  <small>
                    {supplier.document_number
                      ? (supplier.document_type ?? "Doc.") +
                        " " +
                        supplier.document_number
                      : "Sin documento"}
                  </small>
                </div>
                <div className="masters-meta">
                  <span>{supplier.email ?? "Sin correo"}</span>
                  <span>{supplier.phone ?? supplier.city ?? "—"}</span>
                  <div className="quotation-actions">
                    <Link
                      className="compact-button"
                      to={`/proveedores/${supplier.id}`}
                    >
                      Abrir ficha
                    </Link>
                    <button
                      className="compact-button"
                      type="button"
                      disabled={busy === "supplier"}
                      onClick={() => void startEditSupplier(supplier.id)}
                    >
                      Editar
                    </button>
                    <button
                      className="compact-button"
                      type="button"
                      disabled={busy === "supplier"}
                      onClick={() => void changeSupplierStatus(supplier)}
                    >
                      {supplier.status === "active" ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="panel-card">
          <div className="section-heading masters-heading">
            <div>
              <span className="eyebrow">Almacenes</span>
              <h2>{loading ? "…" : warehouses.length} registrados</h2>
            </div>
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                if (showWarehouseForm) {
                  resetWarehouseForm();
                } else {
                  resetWarehouseForm();
                  setShowWarehouseForm(true);
                }
              }}
            >
              {showWarehouseForm ? "Cerrar" : "Nuevo almacén"}
            </button>
          </div>

          {showWarehouseForm ? (
            <form className="masters-form" onSubmit={createWarehouse}>
              <label>
                Código
                <input
                  required
                  value={warehouseCode}
                  onChange={(event) => setWarehouseCode(event.target.value)}
                  placeholder="ALM-01"
                />
              </label>

              <label>
                Nombre
                <input
                  required
                  value={warehouseName}
                  onChange={(event) => setWarehouseName(event.target.value)}
                />
              </label>

              <label className="masters-wide">
                Dirección
                <input
                  value={warehouseAddress}
                  onChange={(event) => setWarehouseAddress(event.target.value)}
                />
              </label>

              <div className="masters-actions">
                <button
                  className="primary-button"
                  type="submit"
                  disabled={busy === "warehouse"}
                >
                  {busy === "warehouse"
                    ? "Guardando…"
                    : editingWarehouseId
                      ? "Guardar cambios"
                      : "Guardar almacén"}
                </button>
                {editingWarehouseId ? (
                  <button
                    className="ghost-button"
                    type="button"
                    onClick={resetWarehouseForm}
                  >
                    Cancelar edición
                  </button>
                ) : null}
              </div>
            </form>
          ) : null}

          <input
            className="masters-search"
            type="search"
            placeholder="Buscar almacén"
            value={warehouseSearch}
            onChange={(event) => setWarehouseSearch(event.target.value)}
          />

          <div className="masters-list">
            {warehouses.map((warehouse) => (
              <article key={warehouse.id}>
                <div>
                  <span className="status-badge">{warehouse.status}</span>
                  <strong>
                    {warehouse.code} · {warehouse.name}
                  </strong>
                  <small>{warehouse.address ?? "Sin dirección"}</small>
                </div>
                <div className="masters-meta">
                  <span>{Number(warehouse.stock_items)} SKU con stock</span>
                  <span>
                    {Number(warehouse.total_quantity).toLocaleString("es-PE")} uds.
                  </span>
                  <div className="quotation-actions">
                    <button
                      className="compact-button"
                      type="button"
                      disabled={busy === "warehouse"}
                      onClick={() => startEditWarehouse(warehouse)}
                    >
                      Editar
                    </button>
                    <button
                      className="compact-button"
                      type="button"
                      disabled={busy === "warehouse"}
                      onClick={() => void changeWarehouseStatus(warehouse)}
                    >
                      {warehouse.status === "active" ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
